/**
 * ONE number per fact, for every project page (row ac33e74b).
 *
 * Keystone gave one fact a different number on almost every page, because each
 * page counted for itself:
 *
 *   - Requirements said "20 approved" (the 20 rows it had loaded, of 30) and
 *     "0%" with no word saying 0% of WHAT; Analytics said "Requirements 100%".
 *     Both were right about different facts: 100% are approved, 0% are done.
 *   - Workstreams looked for Draft / In Progress / Delivered only. All six were
 *     Approved, so every bucket read 0 under "6 workstreams".
 *   - The test cards counted the authoring status (DRAFT) where the reports read
 *     the run verdicts, and the pies printed the raw code "DRAFT".
 *
 * Now the counts come from ONE server response — GET
 * /analytics/project/{id}/summary, whole-project, by real status — and this
 * module is the one place a status is given a bucket and a plain-word label.
 * Requirements and tasks reuse the board's lane vocabulary (kanbanStatus.ts),
 * which Bob set on 2026-09-02, so a card sits in the same lane on the board and
 * in every count. A page that needs a number for one of these facts derives it
 * here; it does not count rows it happens to have fetched.
 */
import { toKanban, KANBAN_STATUSES, type KanbanStatus } from "@/lib/kanbanStatus";
import type { ProjectSummary } from "@/api/services";

/** A labelled count. `label` is customer copy: never a raw status code. */
export interface FactBucket {
  label: string;
  count: number;
}

export interface ProgressFact {
  /** Every row the project holds, not the page that was loaded. */
  total: number;
  /** Agreed: approved, or anything that has moved on from approval. */
  approved: number;
  /** Finished. */
  done: number;
  /** Rounded share of `total` that is done; 0 when there is nothing. */
  donePct: number;
  /** Rounded share of `total` that is approved. */
  approvedPct: number;
  /** Non-empty buckets in reading order, each with its plain-word label. */
  buckets: FactBucket[];
  /** The raw status codes at least one row holds (upper case), for filters
   *  that should offer only what the project actually has. */
  heldStatuses?: string[];
}

export interface TestFact {
  total: number;
  passed: number;
  failed: number;
  blocked: number;
  notRun: number;
  /** Rounded share of `total` whose latest run passed. */
  passedPct: number;
  buckets: FactBucket[];
}

export interface ProjectFacts {
  requirements: ProgressFact;
  tasks: ProgressFact;
  tests: TestFact;
  workstreams: ProgressFact;
  milestones: { total: number; buckets: FactBucket[] };
}

const pct = (n: number, total: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

/** Lanes that mean "agreed": approval and everything that follows it. */
const APPROVED_LANES = new Set<KanbanStatus>(["Approved", "In Progress", "In Review", "Done"]);

function laneFact(byStatus: Record<string, number> | undefined, total: number): ProgressFact {
  const lanes = new Map<KanbanStatus, number>();
  for (const [status, n] of Object.entries(byStatus ?? {})) {
    const lane = toKanban(status);
    lanes.set(lane, (lanes.get(lane) ?? 0) + n);
  }
  const done = lanes.get("Done") ?? 0;
  let approved = 0;
  for (const [lane, n] of lanes) if (APPROVED_LANES.has(lane)) approved += n;
  const order: KanbanStatus[] = [...KANBAN_STATUSES, "Unrecognised"];
  const buckets = order
    .filter((lane) => (lanes.get(lane) ?? 0) > 0)
    .map((lane) => ({ label: lane, count: lanes.get(lane) ?? 0 }));
  const heldStatuses = Object.entries(byStatus ?? {})
    .filter(([, n]) => n > 0)
    .map(([status]) => status.toUpperCase());
  return {
    total, approved, done, donePct: pct(done, total), approvedPct: pct(approved, total), buckets,
    heldStatuses,
  };
}

/** Workstream (module) statuses, named as the status registry names them. */
const WORKSTREAM_BUCKETS: { label: string; statuses: string[] }[] = [
  { label: "Draft", statuses: ["SKETCH", "DRAFT"] },
  { label: "In Review", statuses: ["REVIEW", "IN_REVIEW"] },
  { label: "Approved", statuses: ["APPROVED"] },
  { label: "In Progress", statuses: ["ACTIVE", "IN_PROGRESS"] },
  { label: "Delivered", statuses: ["DELIVERED"] },
  { label: "Archived", statuses: ["ARCHIVED"] },
];

function workstreamFact(byStatus: Record<string, number> | undefined, total: number): ProgressFact {
  const counts = byStatus ?? {};
  const known = new Set(WORKSTREAM_BUCKETS.flatMap((b) => b.statuses));
  const buckets: FactBucket[] = WORKSTREAM_BUCKETS.map((b) => ({
    label: b.label,
    count: b.statuses.reduce((s, st) => s + (counts[st] ?? 0), 0),
  }));
  const other = Object.entries(counts)
    .filter(([st]) => !known.has(st))
    .reduce((s, [, n]) => s + n, 0);
  if (other > 0) buckets.push({ label: "Other", count: other });
  const get = (label: string) => buckets.find((b) => b.label === label)?.count ?? 0;
  const done = get("Delivered");
  const approved = get("Approved") + get("In Progress") + done;
  return {
    total,
    approved,
    done,
    donePct: pct(done, total),
    approvedPct: pct(approved, total),
    buckets: buckets.filter((b) => b.count > 0),
  };
}

const MILESTONE_LABELS: Record<string, string> = {
  PENDING: "Pending",
  ON_TRACK: "On Track",
  PLANNED: "Planned",
  IN_PROGRESS: "In Progress",
  AT_RISK: "At Risk",
  MISSED: "Missed",
  COMPLETED: "Completed",
  ACHIEVED: "Achieved",
  CANCELLED: "Cancelled",
};

/** Derive every project fact from the one summary response. */
export function deriveProjectFacts(summary: ProjectSummary | undefined | null): ProjectFacts {
  const c = summary?.counts ?? {};
  const v = c.test_cases?.by_verdict ?? {};
  const testTotal = c.test_cases?.total ?? 0;
  const passed = v.passed ?? 0;
  const failed = v.failed ?? 0;
  const blocked = (v.blocked ?? 0) + (v.skipped ?? 0);
  const notRun = v.not_run ?? Math.max(0, testTotal - passed - failed - blocked);
  const msByStatus = c.milestones?.by_status ?? {};
  return {
    requirements: laneFact(c.user_stories?.by_status, c.user_stories?.total ?? 0),
    tasks: laneFact(c.tasks?.by_status, c.tasks?.total ?? 0),
    tests: {
      total: testTotal,
      passed,
      failed,
      blocked,
      notRun,
      passedPct: pct(passed, testTotal),
      buckets: [
        { label: "Passed", count: passed },
        { label: "Failed", count: failed },
        { label: "Blocked", count: blocked },
        { label: "Not run", count: notRun },
      ].filter((b) => b.count > 0),
    },
    workstreams: workstreamFact(c.modules?.by_status, c.modules?.total ?? 0),
    milestones: {
      total: c.milestones?.total ?? 0,
      buckets: Object.entries(msByStatus)
        .filter(([, n]) => n > 0)
        .map(([st, n]) => ({ label: MILESTONE_LABELS[st] ?? plainStatus(st), count: n })),
    },
  };
}

/** The dot colour for a bucket label, so every summary bar colours a fact the
 *  same way. Unknown labels get the neutral slate. */
export const FACT_DOT: Record<string, string> = {
  "Blocked — Awaiting User Q&A": "bg-amber-500",
  "Blocked — Awaiting User Approval": "bg-amber-400",
  Approved: "bg-blue-500",
  "In Progress": "bg-indigo-500",
  "In Review": "bg-violet-500",
  Done: "bg-emerald-500",
  Failed: "bg-red-500",
  "To Do (tabled)": "bg-slate-400",
  Unrecognised: "bg-fuchsia-500",
  Draft: "bg-slate-400",
  Delivered: "bg-emerald-500",
  Archived: "bg-gray-400",
  Passed: "bg-emerald-500",
  Blocked: "bg-slate-500",
  "Not run": "bg-amber-500",
};

/** A status code in plain words, for a code no map above names. */
export function plainStatus(code: string): string {
  const words = code.replace(/[_\s]+/g, " ").trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : code;
}
